import Button from "@/components/common/Button";
import I18Translation from "@/components/I18Translation";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";
import HRMSLogo from "@/projects/Hrms/assets/Universal_Logo.png";
import { LogoutOutlined } from "@ant-design/icons";
import { ErrorBoundary, ProLayout } from "@ant-design/pro-components";
import { Dropdown, Input } from "antd";
import {
  Bell,
  Briefcase,
  CalendarCheck,
  CalendarMinus,
  DollarSign,
  History,
  LogOut,
  PieChart,
  Search,
  Settings,
  UserSearch,
  Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

const CustomAvatar = () => {
  const { isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<any>(null);
  useEffect(() => {
    const userInfo = getUserInfo();
    setUserInfo(userInfo);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        menu={{
          items: [
            {
              key: "theme",
              icon: <ThemeSwitcher />,
            },
            {
              key: "language",
              icon: <I18Translation />,
            },
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Logout",
              onClick: handleLogout,
              danger: true,
            },
          ],
        }}
      >
        <div className="flex cursor-pointer items-center gap-2">
          <img
            src="https://i.pravatar.cc/32"
            alt="avatar"
            className="h-8 w-8 rounded-full"
          />
          <span className="text-sm font-medium">
            {userInfo?.first_name} {userInfo?.last_name}
          </span>
        </div>
      </Dropdown>
    </div>
  );
};

const HRMSMainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItemsUility = [
    {
      path: "/",
      name: "Dashboard",
      key: "1",
      icon: <Briefcase size={16} />,
    },
    {
      path: "/hrms/employeelist",
      name: "Employee",
      key: "2",
      icon: <Briefcase size={16} />,
    },
    {
      path: "/hrms/leavelist",
      name: "Leaves",
      key: "3",
      icon: <CalendarMinus size={16} />,
    },
    {
      path: "/hrms/performancelist",
      name: "Performance",
      key: "4",
      icon: <PieChart size={16} />,
    },
    {
      path: "/hrms/employee-lifecycle-list",
      name: "Employee Lifecycle",
      key: "5",
      icon: <History size={16} />,
    },
    {
      path: "recruitment",
      name: "Recruitment",
      key: "6",
      icon: <UserSearch size={16} />,
    },

    {
      path: "attendance",
      name: "Attendance",
      key: "7",
      icon: <CalendarCheck size={16} />,
    },

    {
      path: "expense",
      name: "Expense",
      key: "8",
      icon: <DollarSign size={16} />,
    },
    {
      path: "payroll",
      name: "Payroll",
      key: "9",
      icon: <Wallet size={16} />,
    },
    {
      path: "settings",
      name: "Settings",
      key: "10",
      icon: <Settings size={16} />,
    },
    {
      name: "Log Out",
      key: "11",
      icon: <LogOut size={16} />,
      onClick: () => {},
    },
  ];

  const [settings] = useState({
    layout: "mix" as const,
    fixSiderbar: true,
    splitMenus: false,
  });

  return (
    <ProLayout
      className="hrms-main-layout"
      logo={HRMSLogo}
      title={"HRMS Dashboard"}
      route={{ path: "/", routes: menuItemsUility ?? [] }}
      location={location}
      navTheme="light"
      fixedHeader
      menuItemRender={(item, dom) => (
        <div onClick={() => navigate(item.path!)}>{dom}</div>
      )}
      headerRender={() => (
        <div className="my-auto flex h-full w-full items-center justify-between gap-5 p-4">
          <div className="flex items-center gap-5">
            <img src={HRMSLogo} alt="HRMS Logo" className="h-12 w-28" />
            <Input
              placeholder="Search Post Here"
              prefix={<Search size={16} />}
              style={{ maxWidth: "200px" }}
            />
          </div>
          <div className="flex items-center gap-5">
            <Button tooltip="Notifications" icon={<Bell size={16} />} />
            <CustomAvatar />
          </div>
        </div>
      )}
      menuFooterRender={() => (
        <div className="text-center text-sm text-gray-400">
          © {new Date().getFullYear()} Universal Software
        </div>
      )}
      token={{
        sider: {
          colorMenuBackground: "#fff",
          colorBgMenuItemSelected: "#EDEDFD",
          colorBgMenuItemHover: "#EDEDFD",
          colorTextMenuActive: "#635DE8",
          colorTextMenuSelected: "#635DE8",
          colorTextMenu: "#66697C",
        },
        header: {
          colorBgHeader: "#F8F8FE",
          colorHeaderTitle: "#635DE8",
        },
        bgLayout: "#F8F8FE",
      }}
      {...settings}
    >
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </ProLayout>
  );
};

export default HRMSMainLayout;
